import { useMutation, UseMutationResult, useQuery, UseQueryResult } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useAxiosClientsContextProvider } from 'shared/axios/axios-clients-provider';

import DomipConfiguration from 'features/systems/domip/domain/models/domip-configuration';
import ReadDomipConfigurationResponse from 'features/systems/domip/domain/models/read-domip-configuration-response';
import CreateDomipDeviceCommand from '../models/create-domip-device-command';
import DomipDevice from '../models/domip-device';

const domipBaseUrl = '/api/v1/Domip';

enum DomipQueryPaths {
  UpdateDomipConfiguration = 'UpdateDomipConfiguration',
  ReadDomipConfiguration = 'ReadDomipConfiguration',
  CreateDomipDevice = 'CreateDomipDevice',
}

const createDomipDevice = async (
  axiosInstance: AxiosInstance,
  createDomipDeviceCommand: CreateDomipDeviceCommand,
): Promise<DomipDevice> => {
  const body = JSON.stringify(createDomipDeviceCommand);
  const response = await axiosInstance.post(`${domipBaseUrl}/${DomipQueryPaths.CreateDomipDevice}`, body);
  return response.data;
};

const updateDomipConfiguration = async (
  axiosInstance: AxiosInstance,
  domipConfiguration: DomipConfiguration,
): Promise<DomipConfiguration> => {
  const body = JSON.stringify(domipConfiguration);
  const response = await axiosInstance.put(`${domipBaseUrl}/${DomipQueryPaths.UpdateDomipConfiguration}`, body);
  return response.data;
};

const readDomipConfiguration = async (
  axiosInstance: AxiosInstance,
  systemId: string,
  signal?: AbortSignal,
): Promise<ReadDomipConfigurationResponse> => {
  const response = await axiosInstance.get(`${domipBaseUrl}/${DomipQueryPaths.ReadDomipConfiguration}/${systemId}`, { signal });
  return response.data;
};

export const useCreateDomipDevice = (): UseMutationResult<DomipDevice, Error, CreateDomipDeviceCommand, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DomipQueryPaths.CreateDomipDevice],
    mutationFn: (createDomipDeviceCommand: CreateDomipDeviceCommand) => createDomipDevice(authenticatedInstance, createDomipDeviceCommand),
  });
};

export const useUpdateDomipConfiguration = (): UseMutationResult<DomipConfiguration, Error, DomipConfiguration, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useMutation({
    mutationKey: [DomipQueryPaths.UpdateDomipConfiguration],
    mutationFn: (domipConfiguration: DomipConfiguration) => updateDomipConfiguration(authenticatedInstance, domipConfiguration),
  });
};

export const useLazyReadDomipConfiguration = (systemId: string): UseQueryResult<ReadDomipConfigurationResponse, unknown> => {
  const { authenticatedInstance } = useAxiosClientsContextProvider();
  return useQuery({
    queryKey: [DomipQueryPaths.ReadDomipConfiguration, systemId],
    queryFn: ({ signal }) => readDomipConfiguration(authenticatedInstance, systemId, signal),
    enabled: false,
  });
};
